import { useEffect, useRef, useState } from 'react';
import {
  MAX_BUCKET_NAME_LENGTH,
  type BucketDocumentDraft,
  type BucketProfileFact,
  type SmartFillBucket
} from '@oysterjs/smartfill-contracts';
import { useParams } from 'react-router-dom';
import { useSidebarSession } from '../bridge/session';
import { BucketProfilePattern } from '../components/BucketProfilePattern';
import { BucketTree } from '../components/BucketTree';
import { type PropertyDraft, type SectionDraft } from '../buckets/document';
import { DeleteBucketConfirmation } from '../components/DeleteBucketConfirmation';
import { LearnedChanges } from '../components/LearnedChanges';
import { VisibilitySelect } from '../components/VisibilitySelect';
import { TrashIcon } from '../components/Icons';
import { InlineError, RetryButton, SidebarState } from '../components/SidebarState';
import { useBucket, useDeleteBucket } from '../sidebar/data';
import { appendChildEntity, deleteEntity, deleteProperty, upsertProperty } from '../buckets/edits';
import { setProfileFact } from '../buckets/profiles';
import { useBucketProfiles } from '../sidebar/profiles';
import { useSidebarHistory } from '../sidebar/history';
import { BUCKETS_PATH } from '../sidebar/routes';
import {
  bucketInitial,
  formatRelativeTime,
  pluralize,
  truncateChars
} from '../sidebar/presentation';
import { countDocumentProperties } from '../buckets/document';
import { displayError, type DisplayError } from '../bridge/errors';

export function BucketDetailView() {
  const { bucketId } = useParams();
  const bucketState = useBucket(bucketId);
  const history = useSidebarHistory();

  if (bucketState.loading && !bucketState.bucket) {
    return (
      <SidebarState
        compact
        kind="loading"
        title="Loading bucket"
        description="Getting the latest fields."
      />
    );
  }

  if (bucketState.error || !bucketState.bucket) {
    return (
      <SidebarState
        compact
        kind="error"
        title="Bucket could not load"
        description={bucketState.error?.message ?? 'This bucket is no longer available.'}
        action={
          <div className="state-actions">
            {bucketState.error?.retryable && <RetryButton onClick={bucketState.reload} />}
            <button
              type="button"
              className="button button--secondary button--pill"
              onClick={() => history.go(BUCKETS_PATH)}
            >
              Back to data buckets
            </button>
          </div>
        }
      />
    );
  }

  return (
    <BucketDetail
      bucket={bucketState.bucket}
      setBucket={bucketState.setBucket}
      reload={bucketState.reload}
    />
  );
}

function BucketDetail({
  bucket,
  setBucket,
  reload
}: {
  bucket: SmartFillBucket;
  setBucket: (bucket: SmartFillBucket | null) => void;
  reload: () => void;
}) {
  const { client } = useSidebarSession();
  const history = useSidebarHistory();
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(bucket.name);
  const [propertyDraft, setPropertyDraft] = useState<PropertyDraft | null>(null);
  const [sectionDraft, setSectionDraft] = useState<SectionDraft | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const saving = useRef(false);
  const [error, setError] = useState<DisplayError | null>(null);
  const deletion = useDeleteBucket();
  const profiles = useBucketProfiles();
  const fieldCount = countDocumentProperties(bucket.document);

  useEffect(() => {
    setNameDraft(bucket.name);
  }, [bucket.name]);

  const updateBucket = async (changes: {
    name?: string;
    visibility?: SmartFillBucket['visibility'];
    document?: BucketDocumentDraft;
  }): Promise<boolean> => {
    if (saving.current) return false;
    saving.current = true;
    const original = bucket;
    const optimistic: SmartFillBucket = {
      ...bucket,
      ...(changes.name !== undefined ? { name: changes.name } : {}),
      ...(changes.visibility !== undefined ? { visibility: changes.visibility } : {})
    };
    setBucket(optimistic);
    setBusy(true);
    setError(null);
    try {
      const updated = await client.request('buckets.update', {
        bucketId: original.id,
        expectedRevision: original.revision,
        ...changes
      });
      setBucket(updated);
      return true;
    } catch (requestError) {
      setBucket(original);
      setError(displayError(requestError, 'Could not update this bucket.'));
      return false;
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const saveRename = async () => {
    if (saving.current) return;
    const name = nameDraft.trim();
    if (!name) {
      setError({
        code: 'INVALID_REQUEST',
        message: 'Bucket name is required.',
        retryable: false
      });
      return;
    }
    if (name === bucket.name || (await updateBucket({ name }))) setRenaming(false);
  };

  const saveProperty = async () => {
    if (!propertyDraft) return;
    const displayLabel = propertyDraft.displayLabel.trim();
    if (!displayLabel) {
      setError({
        code: 'INVALID_REQUEST',
        message: 'Enter a field label.',
        retryable: false
      });
      return;
    }
    const document = upsertProperty(bucket.document, { ...propertyDraft, displayLabel });
    if (await updateBucket({ document })) setPropertyDraft(null);
  };

  const removeProperty = async () => {
    if (!propertyDraft?.propertyId) return;
    const document = deleteProperty(
      bucket.document,
      propertyDraft.entityId,
      propertyDraft.propertyId
    );
    if (await updateBucket({ document })) setPropertyDraft(null);
  };

  const saveSection = async () => {
    if (!sectionDraft) return;
    const label = sectionDraft.label.trim();
    if (!label) {
      setError({
        code: 'INVALID_REQUEST',
        message: 'Enter a section name.',
        retryable: false
      });
      return;
    }
    const document = appendChildEntity(bucket.document, sectionDraft.parentEntityId, label);
    if (await updateBucket({ document })) setSectionDraft(null);
  };

  const setFact = (fact: BucketProfileFact, value: string, entityId: string | null) =>
    updateBucket({ document: setProfileFact(bucket.document, fact, value, entityId) });

  const removeEntity = async (entityId: string) => {
    const document = deleteEntity(bucket.document, entityId);
    await updateBucket({ document });
  };

  const deleteBucket = async () => {
    if (busy) return;
    if (await deletion.remove(bucket.id)) history.go(BUCKETS_PATH);
  };

  return (
    <section className="bucket-detail" aria-labelledby="bucket-detail-name">
      <header className="bucket-detail__header">
        <span className="bucket-avatar bucket-avatar--large">{bucketInitial(bucket.name)}</span>
        <div className="bucket-detail__title">
          {renaming ? (
            <form
              id="rename-bucket"
              onSubmit={(event) => {
                event.preventDefault();
                void saveRename();
              }}
            >
              <input
                autoFocus
                aria-label="Bucket name"
                value={nameDraft}
                onChange={(event) =>
                  setNameDraft(truncateChars(event.target.value, MAX_BUCKET_NAME_LENGTH))
                }
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setRenaming(false);
                }}
              />
            </form>
          ) : (
            <h1 id="bucket-detail-name">{bucket.name}</h1>
          )}
          <p>
            {fieldCount} {pluralize(fieldCount, 'field')} · updated{' '}
            {formatRelativeTime(bucket.updatedAt)}
          </p>
        </div>
        {/* Distinct keys keep the Rename click target from becoming a submit button mid-click. */}
        {bucket.canEdit &&
          (renaming ? (
            <button
              key="save-name"
              type="submit"
              form="rename-bucket"
              className="small-pill"
              disabled={busy}
            >
              Save
            </button>
          ) : (
            <button
              key="rename-name"
              type="button"
              className="small-pill"
              onClick={() => {
                setNameDraft(bucket.name);
                setRenaming(true);
              }}
            >
              Rename
            </button>
          ))}
      </header>

      <label className="visibility-control">
        <span>
          <strong>Visibility</strong>
          <small>{bucket.visibility === 'PRIVATE' ? 'Only me' : 'My agency'}</small>
        </span>
        <VisibilitySelect
          ariaLabel="Bucket visibility"
          value={bucket.visibility}
          disabled={!bucket.canEdit || busy}
          onChange={(visibility) => void updateBucket({ visibility })}
        />
      </label>

      <LearnedChanges bucket={bucket} onReverted={setBucket} />

      {deletion.error && <InlineError message={deletion.error.message} />}
      {error && (
        <InlineError
          message={error.message}
          onRetry={error.code === 'CONFLICT' ? reload : undefined}
          retryLabel="Reload"
        />
      )}

      {profiles.length > 0 && (
        <BucketProfilePattern
          key={`profile:${bucket.id}`}
          bucket={bucket}
          profiles={profiles}
          busy={busy}
          onSetFact={setFact}
        />
      )}

      <BucketTree
        key={bucket.id}
        mode="edit"
        stateKey={`edit:${bucket.id}`}
        document={bucket.document}
        busy={busy}
        propertyDraft={propertyDraft}
        sectionDraft={sectionDraft}
        edit={
          bucket.canEdit
            ? {
                onEditProperty: setPropertyDraft,
                onEditSection: setSectionDraft,
                onSaveProperty: () => void saveProperty(),
                onCancelProperty: () => setPropertyDraft(null),
                onRemoveProperty: () => void removeProperty(),
                onSaveSection: () => void saveSection(),
                onCancelSection: () => setSectionDraft(null),
                onRemoveEntity: (entityId) => void removeEntity(entityId)
              }
            : null
        }
      />

      {bucket.canEdit && (
        <div className="delete-section">
          {!deleting ? (
            <button
              type="button"
              className="delete-link"
              disabled={busy}
              onClick={() => setDeleting(true)}
            >
              <TrashIcon />
              Delete data bucket
            </button>
          ) : (
            <DeleteBucketConfirmation
              name={bucket.name}
              busy={busy || deletion.deletingId !== null}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void deleteBucket()}
            />
          )}
        </div>
      )}
    </section>
  );
}
